
import { Component, Vue } from 'vue-property-decorator'
import debounce from 'lodash/debounce'
import { Table } from 'element-ui'
import {
  listBulkGrantCleanup,
  getBulkGrantCleanupMatchingIds,
  getBulkGrantCleanupStats,
  getBulkGrantCleanupUserDetail,
  revokeBulkGrants,
  restoreBulkGrants,
  markBulkGrantVerified
} from '@/api/bulkGrantCleanup'
import { clearUserOtaOverride } from '@/api/userOtaOverride'
import PinList, { PinListItem } from '@/components/ota/PinList.vue'
import PinForm from '@/components/ota/PinForm.vue'

@Component({ name: 'BulkGrantCleanup', components: { PinList, PinForm } })
export default class extends Vue {
  private list: any[] = []
  private listLoading = false
  private actionLoading = false
  private total = 0
  private stats: any = null
  private selected: any[] = []
  private detailVisible = false
  private detailRow: any = null
  private detail: any = null
  private detailLoading = false

  private activeTab: 'pending' | 'removed' = 'pending'

  // Per-pin delete button loading key — 'user-scope' for NULL-serial pin,
  // serial number for device-scoped pins. Matches PinList's row :key so only
  // the row being deleted shows the spinner. Pin save/select state now lives
  // inside PinForm.vue, so we no longer track otaSelection/otaReason/etc here.
  private removingPinKey: string | null = null

  private listQuery: any = {
    page: 1,
    pageSize: 50,
    country: undefined,
    deviceType: undefined,
    batchPrefix: undefined,
    featureId: undefined,
    q: undefined
  }

  // Free-text search box — bound to listQuery.q via a debounced handler so
  // we don't refetch on every keystroke. el-input v-model can't directly
  // call a debounced function (it'd debounce the assignment itself), so we
  // keep a separate `searchQ` and propagate to listQuery via onSearchInput.
  // 500ms gives the operator room to keep typing without firing a request
  // for every character; short enough that a deliberate pause still feels
  // responsive.
  private searchQ = ''
  private debouncedSearch = debounce(() => {
    const next = this.searchQ.trim() || undefined
    // Skip the request when the search hasn't actually changed (e.g. user
    // typed and quickly deleted back to the same value). Prevents the
    // double-fetch the operator was seeing when results felt "stuck".
    if (next === this.listQuery.q) return
    this.listQuery.q = next
    this.listQuery.page = 1
    this.fetchList()
  }, 500)

  created() {
    this.fetchStats()
    this.fetchList()
  }

  get filteredList(): any[] {
    return this.list
  }

  private handleTabChange() {
    // Pending ↔ Removed are disjoint pools, the rows behind the user_ids
    // mean entirely different things — drop the selection on tab switch.
    this.clearSelection()
    this.listQuery.page = 1
    this.fetchList()
  }

  private onSearchInput() {
    this.debouncedSearch()
  }

  private onSearchClear() {
    this.searchQ = ''
    this.debouncedSearch.cancel()
    this.listQuery.q = undefined
    this.listQuery.page = 1
    this.fetchList()
  }

  private clearSelection() {
    // Drops both Element UI's internal reserve-selection cache AND our
    // mirror so the toolbar count returns to 0.
    const table = this.$refs.table as InstanceType<typeof Table> | undefined
    if (table && typeof (table as any).clearSelection === 'function') {
      (table as any).clearSelection()
    }
    this.selected = []
  }

  // ACTIVE devices for this user, ordered most-recently-connected first
  // (heartbeat > activation date). Detail endpoint already returns them
  // in this order (COALESCE picker). Source for the device selector.
  get activeDevices(): any[] {
    return (this.detail?.devices || []).filter((d: any) => d.lifecycle_status === 'ACTIVE')
  }

  // Most-recently-connected ACTIVE cane — used as the default target
  // for the pin form and for the "Cihaz versiyonu / installed firmware"
  // info cells at the top of the OTA section.
  get primaryDevice(): any {
    if (this.activeDevices.length) return this.activeDevices[0]
    return this.detail?.devices?.[0] || null
  }

  // Cane hardware version inferred from the serial number — single source
  // of truth because the devices.product_type column can lie. Pattern
  // stays in sync with the backend classifyCaneFromSerial() helper.
  private classifyFromSerial(serial: string | null | undefined): 'V2' | 'V3' | null {
    if (!serial) return null
    const m = String(serial).match(/^WW-OZ-.{4}([567])-.{6}$/)
    if (!m) return null
    return m[1] === '5' ? 'V2' : 'V3'
  }

  get primaryDeviceVersion(): 'V2' | 'V3' | null {
    return this.classifyFromSerial(this.primaryDevice?.serial_number)
  }

  // Active OTA file catalog passed to PinForm. Detail endpoint already filters
  // to active files; PinForm applies the V2/V3 major filter itself based on
  // the cane the admin picks.
  get cleanupOtaFiles(): any[] {
    return this.detail?.ota?.active_files || []
  }

  // Snake_case → normalized PinListItem shape. Single source of truth for both
  // PinList (display + delete) and PinForm (existing-pin pre-fill lookup).
  get cleanupPinItems(): PinListItem[] {
    return (this.detail?.ota?.overrides || []).map((ov: any) => ({
      key: ov.device_serial_number || 'user-scope',
      deviceSerial: ov.device_serial_number,
      otaFileId: ov.ota_file_id,
      appVersion: ov.application_version_name,
      wirelessVersion: ov.wireless_version_name,
      reason: ov.reason,
      setBy: ov.set_by,
      createdAt: ov.created_at
    }))
  }

  get entitlementsByFeature(): any[] {
    if (!this.detail || !this.detail.entitlements) return []
    const map = new Map<string, any>()
    for (const e of this.detail.entitlements) {
      const key = `${e.feature_id}`
      if (!map.has(key)) {
        map.set(key, {
          feature_id: e.feature_id,
          feature_key: e.feature_key,
          feature_name: e.feature_name,
          rows: []
        })
      }
      map.get(key).rows.push(e)
    }
    return Array.from(map.values())
  }

  private async fetchStats() {
    try {
      const { data } = await getBulkGrantCleanupStats()
      this.stats = data.data || null
    } catch {
      this.stats = null
    }
  }

  private async fetchList() {
    this.listLoading = true
    try {
      const { data } = await listBulkGrantCleanup({ ...this.listQuery, tab: this.activeTab })
      this.list = data.data || []
      this.total = data.pagination?.totalCount || 0
    } catch (err) {
      this.list = []
      this.total = 0
    } finally {
      this.listLoading = false
    }
  }

  private filterByFeature(featureId: number | null) {
    this.listQuery.featureId = featureId || undefined
    this.listQuery.page = 1
    this.fetchList()
  }

  private onSelectionChange(rows: any[]) {
    this.selected = rows
  }

  private handleFilter() {
    // User clicked "Filtrele" (or pressed Enter) — flush the debounced
    // search so the in-flight value is captured even if 300ms hasn't
    // elapsed, and the listQuery.q stays consistent with the input.
    this.debouncedSearch.cancel()
    this.listQuery.q = this.searchQ.trim() || undefined
    this.listQuery.page = 1
    this.fetchList()
  }

  private resetFilters() {
    this.searchQ = ''
    this.debouncedSearch.cancel()
    this.listQuery = {
      page: 1,
      pageSize: this.listQuery.pageSize,
      country: undefined,
      deviceType: undefined,
      batchPrefix: undefined,
      featureId: undefined,
      q: undefined
    }
    this.fetchList()
  }

  private async confirmRestore() {
    const ids = this.selected.map(r => r.user_id)
    try {
      await this.$confirm(
        this.$tc('bulkGrantCleanup.restoreConfirm', ids.length, { n: ids.length }) as string,
        this.$t('bulkGrantCleanup.restoreConfirmTitle') as string,
        { confirmButtonText: this.$t('bulkGrantCleanup.yesRestore') as string, cancelButtonText: this.$t('bulkGrantCleanup.cancel') as string }
      )
    } catch {
      return
    }
    this.actionLoading = true
    try {
      const { data } = await restoreBulkGrants(ids, 'bulk_grant_cleanup_panel_restore')
      this.$notify({
        title: this.$t('bulkGrantCleanup.success') as string,
        message: this.$t('bulkGrantCleanup.restoredSummary', { users: data.data?.affectedUsers || 0, rows: data.data?.affectedRows || 0 }) as string,
        type: 'success',
        duration: 3000
      })
      this.clearSelection()
      await this.fetchList()
      await this.fetchStats()
    } catch (err: any) {
      this.$notify({ title: this.$t('bulkGrantCleanup.error') as string, message: err.message || this.$t('bulkGrantCleanup.restoreFailed') as string, type: 'error' })
    } finally {
      this.actionLoading = false
    }
  }

  private onPageChange(p: number) {
    this.listQuery.page = p
    this.fetchList()
  }

  private onSizeChange(s: number) {
    this.listQuery.pageSize = s
    this.listQuery.page = 1
    this.fetchList()
  }

  private async confirmRevoke() {
    const ids = this.selected.map(r => r.user_id)
    try {
      await this.$confirm(
        this.$tc('bulkGrantCleanup.revokeConfirm', ids.length, { n: ids.length }) as string,
        this.$t('bulkGrantCleanup.revokeConfirmTitle') as string,
        { confirmButtonText: this.$t('bulkGrantCleanup.yesRemove') as string, cancelButtonText: this.$t('bulkGrantCleanup.cancel') as string, type: 'warning' }
      )
    } catch {
      return
    }
    this.actionLoading = true
    try {
      const featureId = this.listQuery.featureId
      const { data } = await revokeBulkGrants(ids, 'bulk_grant_cleanup_v1_panel', featureId)
      this.$notify({
        title: this.$t('bulkGrantCleanup.success') as string,
        message: this.$t('bulkGrantCleanup.removedSummary', { users: data.data?.affectedUsers || 0, rows: data.data?.affectedRows || 0 }) as string,
        type: 'success',
        duration: 3000
      })
      this.clearSelection()
      await this.fetchList()
      await this.fetchStats()
    } catch (err: any) {
      this.$notify({ title: this.$t('bulkGrantCleanup.error') as string, message: err.message || this.$t('bulkGrantCleanup.removeFailed') as string, type: 'error' })
    } finally {
      this.actionLoading = false
    }
  }

  // "Filtreye uyan tümünü kaldır": sayfadaki seçimle sınırlı kalmadan, aktif
  // filtreye (cihaz versiyonu, ülke, feature, arama...) uyan TÜM kullanıcıların
  // bulk grant'larını kaldırır. Önce sunucudan eşleşen tüm user_id'ler çekilir
  // (matching-ids), sonra 200'lük parçalar halinde revoke edilir. Böylece çoklu
  // sayfada tek tek seçme derdi olmadan toplu onay yapılabilir.
  private async confirmRevokeAllMatching() {
    let userIds: string[] = []
    let capped = false
    this.actionLoading = true
    try {
      const params = { ...this.listQuery, tab: this.activeTab }
      delete params.page
      delete params.pageSize
      const { data } = await getBulkGrantCleanupMatchingIds(params)
      userIds = data.data?.userIds || []
      capped = !!data.data?.capped
    } catch (err: any) {
      this.actionLoading = false
      this.$notify({ title: this.$t('bulkGrantCleanup.error') as string, message: err.message || this.$t('bulkGrantCleanup.matchingFetchFailed') as string, type: 'error' })
      return
    }
    this.actionLoading = false

    if (userIds.length === 0) {
      this.$message.info(this.$t('bulkGrantCleanup.noMatchingUsers') as string)
      return
    }
    const cappedNote = capped
      ? '\n\n' + this.$t('bulkGrantCleanup.cappedNote', { n: userIds.length }) as string
      : ''
    try {
      await this.$confirm(
        this.$tc('bulkGrantCleanup.revokeAllConfirm', userIds.length, { n: userIds.length }) as string + cappedNote,
        this.$t('bulkGrantCleanup.removeAllMatching') as string,
        { confirmButtonText: this.$t('bulkGrantCleanup.yesRemoveAll') as string, cancelButtonText: this.$t('bulkGrantCleanup.cancel') as string, type: 'warning' }
      )
    } catch {
      return
    }

    this.actionLoading = true
    const featureId = this.listQuery.featureId
    const CHUNK = 200
    let users = 0
    let rows = 0
    try {
      for (let i = 0; i < userIds.length; i += CHUNK) {
        const slice = userIds.slice(i, i + CHUNK)
        const { data } = await revokeBulkGrants(slice, 'bulk_grant_cleanup_v1_panel_bulk', featureId)
        users += data.data?.affectedUsers || 0
        rows += data.data?.affectedRows || 0
      }
      this.$notify({
        title: this.$t('bulkGrantCleanup.success') as string,
        message: this.$t('bulkGrantCleanup.removedAllSummary', { users, rows, n: userIds.length }) as string,
        type: 'success',
        duration: 4000
      })
      this.clearSelection()
      await this.fetchList()
      await this.fetchStats()
    } catch (err: any) {
      this.$notify({ title: this.$t('bulkGrantCleanup.error') as string, message: err.message || this.$t('bulkGrantCleanup.bulkRemoveFailed') as string, type: 'error' })
      await this.fetchList()
      await this.fetchStats()
    } finally {
      this.actionLoading = false
    }
  }

  private async confirmVerify() {
    const ids = this.selected.map(r => r.user_id)
    try {
      await this.$confirm(
        this.$tc('bulkGrantCleanup.verifyConfirm', ids.length, { n: ids.length }) as string,
        this.$t('bulkGrantCleanup.verifyConfirmTitle') as string,
        { confirmButtonText: this.$t('bulkGrantCleanup.yes') as string, cancelButtonText: this.$t('bulkGrantCleanup.cancel') as string }
      )
    } catch {
      return
    }
    this.actionLoading = true
    try {
      await markBulkGrantVerified(ids)
      this.$message.success(this.$tc('bulkGrantCleanup.verifiedMessage', ids.length, { n: ids.length }) as string)
      this.clearSelection()
      await this.fetchList()
      await this.fetchStats()
    } catch (err: any) {
      this.$message.error(err.message || this.$t('bulkGrantCleanup.error') as string)
    } finally {
      this.actionLoading = false
    }
  }

  private async revokeOne() {
    if (!this.detailRow) return
    const row = this.detailRow
    try {
      await this.$confirm(this.$t('bulkGrantCleanup.revokeOneConfirm', { email: row.email }) as string, this.$t('bulkGrantCleanup.revokeOneTitle') as string, {
        confirmButtonText: this.$t('bulkGrantCleanup.yes') as string, cancelButtonText: this.$t('bulkGrantCleanup.cancel') as string, type: 'warning'
      })
    } catch {
      return
    }
    this.actionLoading = true
    try {
      await revokeBulkGrants([row.user_id], 'bulk_grant_cleanup_single', this.listQuery.featureId)
      this.$message.success(this.$t('bulkGrantCleanup.removed') as string)
      this.detailVisible = false
      await this.fetchList()
      await this.fetchStats()
    } catch (err: any) {
      this.$message.error(err.message || this.$t('bulkGrantCleanup.error') as string)
    } finally {
      this.actionLoading = false
    }
  }

  private async restoreOne() {
    if (!this.detailRow) return
    const row = this.detailRow
    try {
      await this.$confirm(this.$t('bulkGrantCleanup.restoreOneConfirm', { email: row.email }) as string, this.$t('bulkGrantCleanup.restore') as string, {
        confirmButtonText: this.$t('bulkGrantCleanup.yesRestore') as string, cancelButtonText: this.$t('bulkGrantCleanup.cancel') as string
      })
    } catch {
      return
    }
    this.actionLoading = true
    try {
      const { data } = await restoreBulkGrants([row.user_id], 'bulk_grant_cleanup_restore')
      this.$message.success(this.$tc('bulkGrantCleanup.restoredRows', data.data?.affectedRows || 0, { n: data.data?.affectedRows || 0 }) as string)
      this.detailVisible = false
      await this.fetchList()
      await this.fetchStats()
    } catch (err: any) {
      this.$message.error(err.message || this.$t('bulkGrantCleanup.error') as string)
    } finally {
      this.actionLoading = false
    }
  }

  private async openDetail(row: any) {
    this.detailRow = row
    this.detail = null
    this.detailVisible = true
    this.detailLoading = true
    try {
      const { data } = await getBulkGrantCleanupUserDetail(row.user_id)
      this.detail = data.data || null
    } catch (err: any) {
      this.$message.error(this.$t('bulkGrantCleanup.detailLoadFailed', { error: err.message || '' }) as string)
    } finally {
      this.detailLoading = false
    }
  }

  // PinForm fires 'saved' after a successful pin create/update — refresh the
  // detail block so the new pin appears in PinList and the list row's OTA
  // column reflects the change.
  private async onPinSaved() {
    if (!this.detailRow) return
    const userId = this.detailRow.user_id
    try {
      const { data } = await getBulkGrantCleanupUserDetail(userId)
      this.detail = data.data || null
      await this.fetchList()
    } catch {
      // Non-fatal — pin write already succeeded; we just couldn't refresh.
    }
  }

  // Row-level delete: PinList emits the normalized item; we keep the confirm
  // dialog + scope-aware DELETE call (deviceSerial vs applyToAllDevices) here
  // because both branches need detailRow.user_id.
  private async removePinRow(p: PinListItem) {
    if (!this.detailRow) return
    const userId = this.detailRow.user_id
    const isUserScope = p.deviceSerial === null
    const scopeLabel = isUserScope ? this.$t('bulkGrantCleanup.allCanesUserScope') as string : p.deviceSerial
    try {
      await this.$confirm(
        this.$t('bulkGrantCleanup.removePinConfirm', { version: p.appVersion, scope: scopeLabel }) as string,
        this.$t('bulkGrantCleanup.removePinTitle') as string,
        { confirmButtonText: this.$t('bulkGrantCleanup.yesRemove') as string, cancelButtonText: this.$t('bulkGrantCleanup.cancel') as string }
      )
    } catch {
      return
    }
    this.removingPinKey = p.key
    try {
      await clearUserOtaOverride(
        userId,
        isUserScope ? undefined : (p.deviceSerial || undefined),
        isUserScope
      )
      this.$message.success(this.$t('bulkGrantCleanup.pinRemoved') as string)
      const { data } = await getBulkGrantCleanupUserDetail(userId)
      this.detail = data.data || null
      await this.fetchList()
    } catch (err: any) {
      this.$message.error(this.$t('bulkGrantCleanup.pinRemoveFailed', { error: err.message || '' }) as string)
    } finally {
      this.removingPinKey = null
    }
  }

  get detailTitle(): string {
    if (!this.detailRow) return this.$t('bulkGrantCleanup.details') as string
    return this.$t('bulkGrantCleanup.detailTitleFor', { name: this.detailRow.email || this.detailRow.user_id }) as string
  }

  // ---- formatting helpers ----
  private deviceTagType(t: string): string {
    if (t === 'V2B') return 'success'
    if (t === 'V2A') return 'success'
    if (t === 'V2') return 'primary'
    if (t === 'V1') return 'info'
    return ''
  }

  // Distinguish admin_grant (bulk) rows from campaign rows. Campaign rows
  // are listed for visibility but the batch revoke action doesn't touch
  // them, so the tag style hints at that ("danger" reads as "will be
  // revoked", "warning" as "will be left alone").
  private grantSourceLabel(t: string | undefined): string {
    if (t === 'campaign') return 'Campaign'
    return 'Bulk'
  }

  private grantSourceTagType(t: string | undefined): string {
    if (t === 'campaign') return 'warning'
    return 'danger'
  }

  private cleanupStatusType(s: string): string {
    if (s === 'verified') return 'success'
    if (s === 'revoked') return 'warning'
    if (s === 'restored') return 'info'
    return ''
  }

  private cleanupStatusLabel(s: string): string {
    if (s === 'pending') return this.$t('bulkGrantCleanup.statusPending') as string
    if (s === 'revoked') return this.$t('bulkGrantCleanup.statusRevoked') as string
    if (s === 'restored') return this.$t('bulkGrantCleanup.statusRestored') as string
    if (s === 'verified') return this.$t('bulkGrantCleanup.statusVerified') as string
    return s
  }

  private auditActionType(a: string): string {
    if (a === 'verified') return 'success'
    if (a === 'revoke') return 'warning'
    if (a === 'restore') return 'info'
    return ''
  }

  private sourceTypeLabel(t: string, ref: string): string {
    if (t === 'admin_grant' && ref && ref.startsWith('bulk-')) return 'admin_grant (bulk)'
    return t || '—'
  }

  private sourceTypeTag(t: string, ref: string): string {
    if (t === 'admin_grant' && ref && ref.startsWith('bulk-')) return 'warning'
    if (t === 'subscription_purchase') return 'success'
    if (t === 'device_connection') return 'success'
    if (t === 'campaign') return ''
    if (t === 'admin_grant') return ''
    return 'info'
  }

  private formatDate(d: string | Date | null | undefined): string {
    if (!d) return ''
    const date = new Date(d)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  private formatRelativeDate(d: string | Date | null): string {
    if (!d) return this.$t('bulkGrantCleanup.never') as string
    const date = new Date(d)
    if (isNaN(date.getTime())) return this.$t('bulkGrantCleanup.never') as string
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
    if (days === 0) return this.$t('bulkGrantCleanup.today') as string
    if (days === 1) return this.$t('bulkGrantCleanup.yesterday') as string
    if (days < 7) return this.$t('bulkGrantCleanup.daysAgo', { n: days }) as string
    if (days < 30) return this.$t('bulkGrantCleanup.weeksAgo', { n: Math.floor(days / 7) }) as string
    return this.formatDate(d)
  }
}
